/**
 * Pagina configurazione sistema — tutti i parametri raggruppati per modulo.
 *
 * Visibile solo ad admin e super_admin.
 * Endpoint: GET/PUT /api/v1/admin/configurazione
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';

interface ConfigItem {
  id: string;
  chiave: string;
  valore: string;
  tipo: string;
  modulo: string;
  descrizione: string;
  valore_default: string | null;
  ordinamento: number;
  sola_lettura: boolean;
  riservato: boolean;
}

interface PermessoItem {
  codice: string;
  modulo: string;
  azione: string;
  descrizione: string | null;
}

const ConfigurazioneAdmin: React.FC = () => {
  const token = localStorage.getItem('token') ?? '';
  const [items, setItems] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('');

  /* Tab Ruoli */
  const [ruoli, setRuoli] = useState<string[]>([]);
  const [allPermessi, setAllPermessi] = useState<PermessoItem[]>([]);
  const [selectedRuolo, setSelectedRuolo] = useState<string | null>(null);
  const [ruoloPermessi, setRuoloPermessi] = useState<Set<string>>(new Set());
  const [savingRuolo, setSavingRuolo] = useState(false);
  const isRuoliTab = activeTab === '__ruoli__';

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/admin/configurazione', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: ConfigItem[] = await res.json();
      setItems(data);
      const vals: Record<string, string> = {};
      for (const item of data) vals[item.chiave] = item.valore;
      setEditValues(vals);
      if (!activeTab && data.length > 0) {
        setActiveTab(data[0].modulo);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  const caricaRuoli = useCallback(async () => {
    try {
      const [ruoliRes, permRes] = await Promise.all([
        fetch('/api/v1/admin/ruoli', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/admin/permessi', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (ruoliRes.ok) {
        const data: string[] = await ruoliRes.json();
        setRuoli(data.filter((r) => r !== 'super_admin'));
      }
      if (permRes.ok) setAllPermessi(await permRes.json());
    } catch { /* best-effort */ }
  }, [token]);

  const caricaPermessiRuolo = useCallback(async (ruolo: string) => {
    try {
      const res = await fetch(`/api/v1/admin/permessi/ruolo/${ruolo}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRuoloPermessi(new Set(data.permessi));
      }
    } catch { /* best-effort */ }
  }, [token]);

  useEffect(() => { void carica(); void caricaRuoli(); }, [carica, caricaRuoli]);

  useEffect(() => {
    if (selectedRuolo) void caricaPermessiRuolo(selectedRuolo);
    else setRuoloPermessi(new Set());
  }, [selectedRuolo, caricaPermessiRuolo]);

  const salva = async (chiave: string) => {
    setSaving(chiave);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/admin/configurazione/${chiave}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ valore: editValues[chiave] }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess(`${chiave} aggiornato`);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSaving(null);
    }
  };

  const reset = async (chiave: string) => {
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/admin/configurazione/${chiave}/reset`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      setSuccess(`${chiave} ripristinato al default`);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    }
  };

  const toggleRuoloPermesso = (codice: string) => {
    setRuoloPermessi((prev) => {
      const next = new Set(prev);
      if (next.has(codice)) next.delete(codice); else next.add(codice);
      return next;
    });
  };

  const salvaRuoloPermessi = async () => {
    if (!selectedRuolo) return;
    setSavingRuolo(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`/api/v1/admin/permessi/ruolo/${selectedRuolo}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ permessi: [...ruoloPermessi] }),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setSuccess(`Permessi ruolo "${selectedRuolo}" salvati`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setSavingRuolo(false);
    }
  };

  const moduli = [...new Set(items.map((i) => i.modulo))];
  const filteredItems = items.filter((i) => i.modulo === activeTab);

  const moduloLabel = (m: string): string => {
    const map: Record<string, string> = {
      core: 'Core', documentale: 'Documentale', fornitori: 'Fornitori',
      magazzino: 'Magazzino', cassa: 'Cassa', ordini: 'Ordini', prezzi: 'Prezzi',
    };
    return map[m] ?? m;
  };

  const isModified = (item: ConfigItem): boolean =>
    editValues[item.chiave] !== item.valore;

  return (
    <div className="space-y-6 p-6">
      <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
        Configurazione
      </h1>

      {error && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}
      {success && <div className="rounded-md bg-green-500/10 p-3 text-sm text-green-700 dark:text-green-400">{success}</div>}

      {loading && <p className="text-sm text-muted-foreground">Caricamento…</p>}

      {!loading && (
        <>
          {/* Tab moduli + Ruoli */}
          <div className="flex flex-wrap gap-2 border-b pb-2">
            {moduli.map((m) => (
              <button
                key={m}
                onClick={() => { setActiveTab(m); setSelectedRuolo(null); }}
                className="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
                style={{
                  backgroundColor: activeTab === m ? 'var(--color-primary)' : 'transparent',
                  color: activeTab === m ? 'white' : 'var(--color-muted-foreground)',
                }}
              >
                {moduloLabel(m)} ({items.filter((i) => i.modulo === m).length})
              </button>
            ))}
            <button
              onClick={() => setActiveTab('__ruoli__')}
              className="rounded-md px-3 py-1.5 text-sm font-medium transition-colors"
              style={{
                backgroundColor: isRuoliTab ? 'var(--color-primary)' : 'transparent',
                color: isRuoliTab ? 'white' : 'var(--color-muted-foreground)',
              }}
            >
              Ruoli
            </button>
          </div>

          {isRuoliTab ? (
            /* === TAB RUOLI === */
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[200px_1fr]">
              {/* Lista ruoli */}
              <div className="space-y-1">
                <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Seleziona ruolo
                </p>
                {ruoli.map((r) => (
                  <button
                    key={r}
                    onClick={() => setSelectedRuolo(r)}
                    className="block w-full rounded-md px-3 py-2 text-left text-sm transition-colors"
                    style={{
                      backgroundColor: selectedRuolo === r ? 'var(--color-primary)' : 'transparent',
                      color: selectedRuolo === r ? 'white' : 'inherit',
                    }}
                  >
                    <span className="capitalize">{r}</span>
                  </button>
                ))}
                {ruoli.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nessun ruolo. Crea degli utenti.</p>
                )}
                <div className="mt-4 rounded-md border border-primary/20 bg-primary/5 p-3">
                  <p className="text-xs text-muted-foreground">
                    <strong>super_admin</strong> e <strong>admin</strong> hanno accesso totale e non compaiono qui.
                  </p>
                </div>
              </div>

              {/* Permessi del ruolo selezionato */}
              {selectedRuolo ? (
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="capitalize">{selectedRuolo}</CardTitle>
                    <Button size="sm" onClick={() => void salvaRuoloPermessi()} disabled={savingRuolo}>
                      {savingRuolo ? '…' : 'Salva permessi'}
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {(() => {
                      const permModuli = [...new Set(allPermessi.map((p) => p.modulo))];
                      const moduloLabelsP: Record<string, string> = {
                        core: 'Sistema', documentale: 'Documentale', fornitori: 'Fornitori',
                        magazzino: 'Magazzino', cassa: 'Cassa', ordini: 'Ordini', prezzi: 'Prezzi',
                      };
                      return permModuli.map((modulo) => (
                        <div key={modulo}>
                          <p className="mb-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            {moduloLabelsP[modulo] ?? modulo}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {allPermessi.filter((p) => p.modulo === modulo).map((perm) => {
                              const active = ruoloPermessi.has(perm.codice);
                              return (
                                <button
                                  key={perm.codice}
                                  onClick={() => toggleRuoloPermesso(perm.codice)}
                                  className="rounded-md border px-2.5 py-1.5 text-xs transition-colors"
                                  style={{
                                    backgroundColor: active ? 'var(--color-primary)' : 'transparent',
                                    color: active ? 'white' : 'var(--color-muted-foreground)',
                                    borderColor: active ? 'var(--color-primary)' : 'var(--color-border)',
                                  }}
                                  title={perm.descrizione ?? ''}
                                >
                                  {perm.descrizione ?? perm.azione}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ));
                    })()}
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex h-48 items-center justify-center">
                    <p className="text-sm text-muted-foreground">
                      Seleziona un ruolo per gestirne i permessi
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            /* === TAB PARAMETRI MODULO === */
            <div className="space-y-4">
              {filteredItems.map((item) => (
                <Card key={item.chiave}>
                  <CardContent className="flex items-start gap-4 p-4">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-medium">{item.chiave}</code>
                        <Badge variant="outline" className="text-xs">{item.tipo}</Badge>
                        {item.sola_lettura && <Badge variant="secondary" className="text-xs">sola lettura</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{item.descrizione}</p>
                      {item.valore_default && (
                        <p className="text-xs text-muted-foreground">Default: {item.valore_default}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {item.tipo === 'boolean' ? (
                        <button
                          onClick={() => {
                            const newVal = editValues[item.chiave] === 'true' ? 'false' : 'true';
                            setEditValues((p) => ({ ...p, [item.chiave]: newVal }));
                          }}
                          disabled={item.sola_lettura}
                          className="rounded-md border px-3 py-1.5 text-sm"
                          style={{
                            backgroundColor: editValues[item.chiave] === 'true' ? 'var(--color-primary)' : 'transparent',
                            color: editValues[item.chiave] === 'true' ? 'white' : 'inherit',
                          }}
                        >
                          {editValues[item.chiave] === 'true' ? 'Attivo' : 'Disattivo'}
                        </button>
                      ) : (
                        <Input
                          value={editValues[item.chiave] ?? ''}
                          onChange={(e) => setEditValues((p) => ({ ...p, [item.chiave]: e.target.value }))}
                          disabled={item.sola_lettura}
                          className="w-48"
                          type={item.tipo === 'integer' || item.tipo === 'decimal' ? 'number' : 'text'}
                        />
                      )}
                      <Button
                        size="sm"
                        onClick={() => void salva(item.chiave)}
                        disabled={item.sola_lettura || saving === item.chiave || !isModified(item)}
                      >
                        {saving === item.chiave ? '…' : 'Salva'}
                      </Button>
                      {item.valore_default && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void reset(item.chiave)}
                          disabled={item.sola_lettura || item.valore === item.valore_default}
                        >
                          Reset
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredItems.length === 0 && (
                <p className="text-sm text-muted-foreground">Nessun parametro per questo modulo.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ConfigurazioneAdmin;
